import type { ReactNode } from 'react';
import { generatePath } from 'react-router-dom';
import routes from '~/routes';
import { LinkWithHover } from 'components/hover-card-resource';
import { PlainLink } from 'components/link';
import { EMPTY_CELL } from 'utils/empty-cell';
import { entityRefLabel } from 'utils/entity-id';

interface EntityLinkProps {
  id: number | null | undefined;
  name: string | null | undefined;
  /** Shown in place of the name when there is no entity. */
  fallback?: ReactNode;
  /** Shows the entity's hover card over the link. */
  hover?: boolean;
  className?: string;
}

/**
 * An entity named the standard way, "<name> (Entity ID: n)", linked to its page
 * in a new tab. A blank name on a real entity reads "Unknown entity", as the
 * activity feed does. With no entity it shows the fallback, else the name as
 * plain text, else an em dash. A click on the link doesn't reach a row around it.
 * @param {EntityLinkProps} props - The entity.
 * @return {JSX.Element} The link, the plain name, or the fallback.
 */
export function EntityLink({
  id,
  name,
  fallback,
  hover = false,
  className,
}: EntityLinkProps) {
  if (!id) return <>{fallback ?? (name?.trim() || EMPTY_CELL)}</>;

  const href = generatePath(routes.entityPath, { id });
  const link = (
    <PlainLink
      className={className}
      href={href}
      openInNewTab
      onClick={(event) => event.stopPropagation()}
    >
      {entityRefLabel(name, id)}
    </PlainLink>
  );
  if (!hover) return link;
  return (
    <LinkWithHover kind="entity" href={href}>
      {link}
    </LinkWithHover>
  );
}
